import { hasTimePassed } from '@/vb-utils/lot-countdown.util';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionCatalogsService,
    LotDetail,
    PurchaseOfferResponse,
    PurchaseOfferStatus,
    Response,
} from '@viewbid/ts-angular-client';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { VbSnackbarService } from '@viewbid/ui';
import { ConfirmationWindowComponent } from 'viewbid-shared-lib';
@Component({
    selector: 'vb-make-offer',
    templateUrl: './make-offer.component.html',
    styleUrls: ['./make-offer.component.scss'],
})
export class MakeOfferComponent implements OnInit {
    @Input() lot!: LotDetail;
    @Output() closeOfferSection: EventEmitter<void> = new EventEmitter<void>();
    @Input() isCounterOffer: boolean = false;
    @Input() offerId!: string;
    offerDetails?: PurchaseOfferResponse;
    quantity: number = 1;
    offerPrice: number = 0;
    offerMessage: string = '';
    maxLength: number = 250;

    modalRef?: BsModalRef;
    isLoading: boolean = false;
    private successToastId: number = 0;
    @ViewChild('dialogContent', { static: true })
    dialogContent!: TemplateRef<any>;

    constructor(
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        private readonly snackbarService: VbSnackbarService,
        private readonly auctionService: AuctionCatalogsService,
        private readonly serverClock: ServerClockService,
    ) {}
    ngOnInit(): void {
        if (this.checkLotClosed()) {
            return;
        }
        if (this.offerId) {
            this.getOfferDetails();
        }
        if (this.lot.id && !this.offerId) {
            this.checkOfferExists();
        }
    }

    get offerStatus(): typeof PurchaseOfferStatus {
        return PurchaseOfferStatus;
    }

    get characterCount(): number {
        return this.offerMessage.length || 0;
    }

    closeComponent(): void {
        this.closeOfferSection.emit();
    }

    decrementQuantity(): void {
        if (this.quantity > 1) {
            this.quantity--;
        }
    }

    incrementQuantity(): void {
        this.quantity++;
    }

    openConfirmationModal(): void {
        if (this.offerPrice > 0 && !this.isValueDecimal(this.offerPrice)) {
            this.modalRef = this.modalService.show(
                ConfirmationWindowComponent,
                {
                    class: 'modal-md custom-modal-width modal-dialog-centered',
                    initialState: {
                        headerText: this.translate.instant(
                            'PurchaseOffers.confirmHeader',
                        ),
                        contentTemplate: this.dialogContent,
                        context: {
                            offer: {
                                quantity: this.quantity,
                                price: this.offerPrice,
                                message: this.offerMessage,
                            },
                        },
                        mainText: this.translate.instant(
                            'PurchaseOffers.confirmMessage',
                        ),
                        showCancelButton: false,
                        additionalActionButtons: [
                            {
                                label: this.translate.instant(
                                    'PurchaseOffers.labelEditOffer',
                                ),
                                class: 'btn cancel-btn bold',
                                action: (): void => {
                                    this.modalRef?.hide();
                                },
                            },
                        ],
                    },
                },
            );

            this.modalRef.content.response.subscribe((response: boolean) => {
                if (response) this.submitOffer();
            });
        } else {
            this.snackbarService.error(
                this.translate.instant('PurchaseOffers.priceValidationMessage'),
            );
        }
    }

    isValueDecimal(price: number): boolean {
        return Boolean(price % 1);
    }

    onKeyPress(event: KeyboardEvent): boolean {
        // Allow only numeric keys (0-9)
        if (event.key >= '0' && event.key <= '9') {
            return true;
        }
        event.preventDefault();
        return false;
    }

    onInputChange(event: Event): void {
        const input: HTMLInputElement = event.target as HTMLInputElement;
        // Remove any non-numeric characters
        input.value = input.value.replace(/\D/g, '');
    }

    private async submitOffer(): Promise<void> {
        this.isLoading = true;
        this.modalRef?.hide();
        this.auctionService
            .submitPurchaseOfferByLot(this.lot.id ?? '', {
                quantity: this.quantity,
                price: this.offerPrice,
                message: this.offerMessage,
            })
            .subscribe({
                next: () => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant('LotDetails.createOfferSuccess'),
                    );
                    this.isLoading = false;
                    this.closeComponent();
                },
                error: (error: HttpErrorResponse) => {
                    this.isLoading = false;
                    const errorMessage: string =
                        this.getOfferErrorMessage(error);
                    this.snackbarService.error(errorMessage);
                },
            });
    }

    onRejectedClick(status: PurchaseOfferStatus): void {
        this.modalRef = this.modalService.show(ConfirmationWindowComponent, {
            class: 'modal-md custom-modal-width modal-dialog-centered',
            initialState: {
                headerText: this.translate.instant('LotDetails.walkAwayTitle'),
                mainText: this.translate.instant('LotDetails.walkAwayMessage'),
            },
        });
        this.modalRef.content.response.subscribe((confirm: boolean) => {
            if (confirm) this.placeCounterOffer(status);
        });
    }

    async placeCounterOffer(status: PurchaseOfferStatus): Promise<void> {
        this.isLoading = true;
        this.auctionService
            .updatePurchaseOffer(
                this.lot.id ?? '',
                this.offerDetails?.id ?? '',
                {
                    quantity: this.quantity,
                    price: this.offerPrice,
                    status: status,
                    message: this.offerMessage,
                },
            )
            .subscribe({
                next: () => {
                    this.snackbarService.dismiss(this.successToastId);
                    let messageKey: string;
                    if (status === this.offerStatus.Accepted) {
                        messageKey = 'LotDetails.offerAccepted';
                    } else if (status === this.offerStatus.Rejected) {
                        messageKey = 'LotDetails.offerRejected';
                    } else {
                        messageKey = 'LotDetails.CounterOfferSubmitted';
                    }
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(messageKey),
                    );
                    this.isLoading = false;
                    this.closeComponent();
                },
                error: (error: HttpErrorResponse) => {
                    this.isLoading = false;
                    const errorMessage: string =
                        this.getOfferErrorMessage(error);
                    this.snackbarService.error(errorMessage);
                },
            });
    }

    toggleCounterOffer(): void {
        this.isCounterOffer = !this.isCounterOffer;
    }

    private async getOfferDetails(): Promise<void> {
        this.isLoading = true;
        this.auctionService.getPurchaseOfferById(this.offerId).subscribe({
            next: (response: { data?: PurchaseOfferResponse }) => {
                this.isLoading = false;
                this.offerDetails = response.data;
            },
            error: () => {
                this.isLoading = false;
                this.snackbarService.error(
                    this.translate.instant(
                        'PurchaseOffers.getPurchaseOfferError',
                    ),
                );
            },
        });
    }

    private async checkOfferExists(): Promise<void> {
        this.isLoading = true;
        this.auctionService.checkExistingOffer(this.lot.id!).subscribe({
            next: (response: Response) => {
                this.isLoading = false;
                if (response.details?.exists) {
                    this.snackbarService.error(
                        this.translate.instant(
                            'PurchaseOffers.offerAlreadyExists',
                        ),
                    );
                    this.closeComponent();
                }
            },
            error: () => {
                this.isLoading = false;
                this.snackbarService.error(
                    this.translate.instant(
                        'PurchaseOffers.getPurchaseOfferError',
                    ),
                );
                this.closeComponent();
            },
        });
    }

    private checkLotClosed(): boolean {
        if (hasTimePassed(this.lot?.toDatetime, this.serverClock.now())) {
            this.snackbarService.error(
                this.translate.instant('PurchaseOffers.lotClosed'),
            );
            this.closeComponent();
            return true;
        }
        return false;
    }

    /**
     * Determines the appropriate error message for offer submission errors.
     */
    private getOfferErrorMessage(error: HttpErrorResponse): string {
        if (error.error?.code === 21050) {
            return this.translate.instant('LotDetails.offerUserUnapproved');
        }

        if (error.error?.message) {
            return error.error.message;
        }

        return this.translate.instant('LotDetails.createOfferError');
    }
}
